import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import EmailBuilderMetadata from "./EmailBuilderMetadata";
import EmailBuilderCanvasBlock from "./EmailBuilderCanvasBlock";
import { canRemoveBlock } from "./builderDraft";
import { CANVAS_END_DROP_ID } from "./builderDnd";

const DEVICE_WIDTH = { desktop: "max-w-[620px]", mobile: "max-w-[375px]" };

/**
 * EmailBuilderCanvas — EMAILS-IMPL-015
 *
 * The CENTER card: fixed Email Metadata (Subject/Preview/Landing Page)
 * above an ordered, vertical Canvas of body blocks. The `SortableContext`
 * here is a descendant of the single `DndContext` CreativeEmailBuilder.jsx
 * owns (spanning the Asset library too) -- this is what makes true
 * library-to-Canvas drop possible, not the Funnel Builder's single-list
 * reorder-only pattern.
 *
 * Desktop/Mobile is container-width-only (matches the Landing Page
 * Builder precedent exactly) -- the same block components render either
 * way, only the surrounding width changes.
 */
export default function EmailBuilderCanvas({ draft, selectedBlockId, onSelectBlock, onRemoveBlock, onChangeFixedSlot, mode, device, readOnly }) {
  const blockIds = draft.canvasLayout.map((b) => b.blockId);

  return (
    <Card className="glass flex h-full flex-col border-white/10" data-testid="builder-canvas-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base text-slate-100">{mode === "preview" ? "Preview" : "Canvas"}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto pt-0">
        <div className={`mx-auto space-y-4 transition-all ${DEVICE_WIDTH[device] || DEVICE_WIDTH.desktop}`} data-testid="builder-canvas-viewport" data-device={device}>
          <EmailBuilderMetadata fingerprint={draft.fingerprint} onChangeSlot={onChangeFixedSlot} readOnly={readOnly} mode={mode} />

          <div data-testid="builder-canvas" className="space-y-2">
            {draft.canvasLayout.length === 0 ? (
              <EmptyCanvasDropZone readOnly={readOnly} />
            ) : (
              <SortableContext items={blockIds} strategy={verticalListSortingStrategy}>
                {draft.canvasLayout.map((block) => (
                  <EmailBuilderCanvasBlock
                    key={block.blockId}
                    block={block}
                    selected={selectedBlockId === block.blockId}
                    removable={canRemoveBlock(draft, block.blockId)}
                    readOnly={readOnly}
                    mode={mode}
                    onSelect={onSelectBlock}
                    onRemove={onRemoveBlock}
                  />
                ))}
              </SortableContext>
            )}
            {!readOnly && draft.canvasLayout.length > 0 && <CanvasEndDropZone />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyCanvasDropZone({ readOnly }) {
  const { setNodeRef, isOver } = useDroppable({ id: CANVAS_END_DROP_ID, disabled: readOnly });
  return (
    <div
      ref={setNodeRef}
      data-testid="builder-canvas-empty"
      className={
        "rounded-lg border-2 border-dashed p-8 text-center text-sm transition-colors " +
        (isOver ? "border-violet-400/60 bg-violet-500/10 text-violet-200" : "border-white/10 text-slate-500")
      }
    >
      {readOnly ? "No composition yet." : "Drag Assets here, or use the Add button in the library."}
    </div>
  );
}

function CanvasEndDropZone() {
  const { setNodeRef, isOver } = useDroppable({ id: CANVAS_END_DROP_ID });
  return (
    <div
      ref={setNodeRef}
      data-testid="builder-canvas-dropzone-end"
      className={"h-6 rounded-md border border-dashed transition-colors " + (isOver ? "border-violet-400/60 bg-violet-500/10" : "border-transparent")}
    />
  );
}
